// src/core/tribuna-vidriera.js — selección "bombo de lotería" + destacados de
// la muralla de la Tribuna. PURO (sin DOM, sin red): testeable aislado.
//
// perf-tribuna vidriera (31-may). Reemplaza la rotación por offset secuencial
// (que mostraba tramos contiguos del ranking, ignorando a C apenas el offset
// pasaba de 0). Dos pools por renovación, sobre las 500 que ya trae feed_tribuna
// ordenadas por el score C (no se toca el backend):
//
//   - DESTACADOS (top-C, re-leído cada renovación): las de mayor score aparecen
//     MÁS seguido (pueden repetir entre renovaciones) → sesgo a lo mejor/nuevo.
//     Como C sube lo nuevo, una arenga recién creada entra acá apenas C la sube.
//   - BOMBO (garantía): barre TODAS las demás SIN repetir hasta completar la
//     vuelta; al completarla, reinicia. Garantiza que ninguna queda afuera.
//
// La memoria del bombo (Set de ids ya mostrados en el ciclo) vive en el caller
// (closure de renderTribuna) y se pasa/recibe acá — esta capa es pura.

// Fisher-Yates con rng inyectable (default Math.random). Determinista en tests.
export const barajar = (arr, rng = Math.random) => {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const tmp = a[i]; a[i] = a[j]; a[j] = tmp
  }
  return a
}

// seleccionarVidriera(ranked, seen, opts) → { seleccion, seen }
//   ranked : arengas DISTINTAS ordenadas por C (las 500 de feed_tribuna).
//   seen   : Set de ids ya mostrados por el BOMBO en el ciclo vigente.
//   opts   : { destacadosN, totalN, getId, rng }.
//   Devuelve la selección (destacados + bombo, SIN barajar — el caller baraja el
//   conjunto completo antes de repartir en filas) y el Set `seen` actualizado
//   (reiniciado a vacío SOLO cuando el bombo completó la vuelta).
export const seleccionarVidriera = (ranked, seen, opts = {}) => {
  const {
    destacadosN = 24,
    totalN = 96,
    getId = (x) => x.id,
    rng = Math.random,
  } = opts
  const bomboN = Math.max(0, totalN - destacadosN)
  if (!ranked || !ranked.length) return { seleccion: [], seen: new Set() }

  const destacados = ranked.slice(0, destacadosN)
  const destIds = new Set(destacados.map(getId))

  // Pool elegible del bombo: ranked que NO es destacado en esta ronda.
  const elegibles = ranked.filter((r) => !destIds.has(getId(r)))
  let nextSeen = new Set(seen)
  // Pendientes = elegibles aún no mostrados por el bombo en este ciclo.
  const pendientes = elegibles.filter((r) => !nextSeen.has(getId(r)))

  let bombo
  if (pendientes.length >= bomboN) {
    // Hay suficientes sin mostrar → todos NUEVOS (cero repetición).
    bombo = barajar(pendientes, rng).slice(0, bomboN)
    bombo.forEach((r) => nextSeen.add(getId(r)))
  } else {
    // No alcanzan: con esta renovación TODOS los elegibles habrán pasado →
    // cerramos la vuelta y REINICIAMOS el ciclo. Los pendientes que quedaban
    // entran enteros; si aún faltan slots para llenar la fila, se completan con
    // ya-vistos (inevitable cuando elegibles no es múltiplo de bomboN), pero la
    // garantía "todos pasaron antes de reiniciar" se cumple en esta misma ronda.
    const restantes = barajar(pendientes, rng)
    nextSeen = new Set() // vuelta completa → el ciclo del bombo reinicia
    const yaEn = new Set(restantes.map(getId))
    const resto = barajar(elegibles.filter((r) => !yaEn.has(getId(r))), rng)
    bombo = restantes.slice()
    for (const r of resto) {
      if (bombo.length >= bomboN) break
      bombo.push(r); yaEn.add(getId(r))
    }
    bombo.forEach((r) => nextSeen.add(getId(r)))
  }

  return { seleccion: [...destacados, ...bombo], seen: nextSeen }
}
